import React from 'react'
import { Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material'
import { CheckIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { ActivityType } from '../../types'
import { shouldShowLoopConfiguration } from '../../utils/activityConfiguration'

interface ActivityDurationFieldProps {
  activityType: ActivityType
  duration?: number
  onDurationChange?: (duration: number) => void
  /** Optional confirm handler — renders a check button and commits on Enter. */
  onDone?: () => void
  autoFocus?: boolean
  errorText?: string
}

/**
 * Compact, inline numeric editor for non-game activity length (introductions and
 * reflections). Game activities derive their length from slot/loop config, so this
 * renders null for them. Intended to be revealed on demand next to the duration label.
 *
 * @param {ActivityDurationFieldProps} props - Component props.
 * @return {JSX.Element | null} Duration editor or null for game activities.
 */
export const ActivityDurationField: React.FC<ActivityDurationFieldProps> = ({
  activityType,
  duration,
  onDurationChange,
  onDone,
  autoFocus = true,
  errorText
}) => {
  const { t } = useTranslation()

  if (shouldShowLoopConfiguration(activityType)) {
    return null
  }

  const hasValidDuration = typeof duration === 'number' && Number.isFinite(duration) && duration > 0

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <TextField
        size="small"
        type="number"
        autoFocus={autoFocus}
        value={hasValidDuration ? duration : ''}
        onChange={(event) =>
          onDurationChange?.(event.target.value === '' ? 0 : Math.max(0, Math.round(Number(event.target.value))))
        }
        onKeyDown={(event) => {
          if (event.key === 'Enter' && hasValidDuration) {
            event.preventDefault()
            onDone?.()
          }
        }}
        error={!!errorText}
        helperText={errorText}
        inputProps={{
          min: 1,
          step: 1,
          inputMode: 'numeric',
          'aria-label': t('facilitator.activityConfig.durationLabel', 'Duration')
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography sx={{ fontSize: '12px', color: INTERAX_COLORS.thunderGrey }}>
                {t('common.minutesShort', 'min')}
              </Typography>
            </InputAdornment>
          )
        }}
        sx={{
          width: 104,
          '& .MuiInputBase-root': {
            backgroundColor: INTERAX_COLORS.heritageBlue40,
            color: INTERAX_COLORS.white,
            borderRadius: '8px'
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: errorText ? INTERAX_COLORS.redError : INTERAX_COLORS.thunderGrey
          }
        }}
      />

      {onDone && (
        <IconButton
          size="small"
          onClick={onDone}
          disabled={!hasValidDuration}
          aria-label={t('common.done', 'Done')}
          sx={{
            p: 0.5,
            color: INTERAX_COLORS.offBlack,
            backgroundColor: INTERAX_COLORS.sun,
            '&:hover': { backgroundColor: INTERAX_COLORS.sun },
            '&.Mui-disabled': { backgroundColor: INTERAX_COLORS.thunderGrey, color: INTERAX_COLORS.white70 }
          }}
        >
          <CheckIcon className="w-4 h-4" />
        </IconButton>
      )}
    </Box>
  )
}
